import type { Archive } from "../../domain/types";
import {
  resolveFeatureRoutes,
  type FeatureRoutingSettings,
} from "../../services/featureRoutes";
import { callLLMWithRoutes } from "../../services/llm";
import {
  MH_IMPORTED_PRESET_ADAPTATION,
  resolvePresetContent,
} from "../../services/presetAssembler";
import { PLOT_PRESETS, usePlotEvolutionStore } from "./store";

interface PlotPresetFile {
  id: string;
  name: string;
  promptTemplate: string;
}
interface DirectorPresetFile {
  strategyPrompt: string;
}
const cache = new Map<string, Promise<PlotPresetFile>>();
function loadPlotPreset(id: string) {
  const entry = PLOT_PRESETS.find((item) => item.id === id) ?? PLOT_PRESETS[0];
  let pending = cache.get(entry.id);
  if (!pending) {
    pending = fetch(`/presets/mortal-approved/plot-evolution/${entry.path}`).then(async (response) => {
      if (!response.ok) throw new Error(`无法载入剧情演化预设：${entry.name}`);
      return (await response.json()) as PlotPresetFile;
    });
    cache.set(entry.id, pending);
  }
  return pending;
}
let directorPreset: Promise<DirectorPresetFile> | null = null;
function loadDirectorPreset() {
  directorPreset ??= fetch(
    "/presets/mortal-approved/plot-turn-director/default.json",
  ).then(async (response) => {
    if (!response.ok) throw new Error("无法载入本轮剧情导演预设");
    return (await response.json()) as DirectorPresetFile;
  });
  return directorPreset;
}

function compactState(archive: Archive) {
  return [
    `时间：第${archive.gameTime.day}日 ${archive.gameTime.hour}:00`,
    `地点：${archive.scene.location}`,
    `玩家：${archive.player.identity.name}，HR ${archive.player.hunter.hr}`,
    `当前任务：${archive.tasks.available.find((task) => task.id === archive.tasks.activeTaskId)?.title ?? "无"}`,
    `在场人物：${Object.values(archive.characters).filter((character) => character.presence.status === "present").map((character) => character.identity.name).join("、") || "无"}`,
    `世界事件：${archive.worldEvents.slice(-8).join("；") || "无"}`,
  ].join("\n");
}
function cleanGuidance(value: string) {
  return value
    .replace(/<think>[\s\S]*?<\/think>/gi, "")
    .replace(/```(?:json)?/gi, "")
    .trim();
}

export async function preparePlotDirector(
  archive: Archive,
  input: string,
  settings: FeatureRoutingSettings,
  signal?: AbortSignal,
) {
  const config = usePlotEvolutionStore.getState();
  if (!config.enabled || !config.directorEnabled || !archive.plotEvolution.guidance.trim())
    return { archive, directive: "" };
  const preset = await loadDirectorPreset();
  const response = await callLLMWithRoutes(
    resolveFeatureRoutes("plot-evolution", settings),
    [
      {
        role: "system",
        content: `${MH_IMPORTED_PRESET_ADAPTATION}\n\n${preset.strategyPrompt}\n\n你只输出不超过 500 字的本回合公开导演指令，不输出后台推理。`,
      },
      {
        role: "user",
        content: `【私密长线档案】\n${archive.plotEvolution.guidance}\n\n【当前状态】\n${compactState(archive)}\n\n【玩家输入】\n${input}`,
      },
    ],
    {
      signal,
      stream: false,
      debug: {
        featureId: "plot-turn-director",
        archiveName: archive.name,
      },
    },
  );
  const directive = cleanGuidance(response.text);
  const next = structuredClone(archive);
  next.plotEvolution.currentDirective = directive;
  next.plotEvolution.updatedAt = Date.now();
  return { archive: next, directive };
}

export async function settlePlotEvolution(
  archive: Archive,
  input: string,
  story: string,
  settings: FeatureRoutingSettings,
  force = false,
) {
  const config = usePlotEvolutionStore.getState();
  if (!force && !config.enabled) return archive;
  const turnCount = archive.messages.filter((message) => message.role === "assistant").length;
  if (!force && archive.plotEvolution.guidance && turnCount % config.frequency !== 0)
    return archive;
  const preset = await loadPlotPreset(config.activePresetId);
  const vars = {
    storyText: story,
    previousPlotEvolutionGuidance: archive.plotEvolution.guidance || "无",
    currentTime: `第${archive.gameTime.day}日 ${archive.gameTime.hour}:00`,
    currentLocation: archive.scene.location,
    sceneInfoBlock: compactState(archive),
    onScreenNpcs: Object.values(archive.characters).filter((character) => character.presence.status === "present").map((character) => characterProfileLine(character)).join("\n") || "无",
    offScreenNpcs: Object.values(archive.characters).filter((character) => character.presence.status !== "present").map((character) => characterProfileLine(character)).join("\n") || "无",
    currentTasks: archive.tasks.available.filter((task) => task.status === "active").map((task) => task.title).join("、") || "无",
    worldEvents: archive.worldEvents.join("\n") || "无",
    narrativeMemory: archive.narrativeMemory.summary || "无",
    playerSnapshot: compactState(archive),
    userInput: input,
    contextHistory: archive.messages.slice(-12).map((message) => `${message.role}: ${message.content}`).join("\n"),
  };
  const prompt = resolvePresetContent(preset.promptTemplate, vars);
  const response = await callLLMWithRoutes(
    resolveFeatureRoutes("plot-evolution", settings),
    [
      { role: "system", content: MH_IMPORTED_PRESET_ADAPTATION },
      { role: "user", content: prompt },
    ],
    {
      stream: false,
      debug: { featureId: "plot-evolution", archiveName: archive.name },
    },
  );
  const guidance = cleanGuidance(response.text);
  if (!guidance) throw new Error("剧情演化没有返回可用指导");
  const next = structuredClone(archive);
  next.plotEvolution.guidance = guidance;
  next.plotEvolution.directorLedger = guidance;
  next.plotEvolution.guidanceUpdatedTurn = turnCount;
  next.plotEvolution.updatedAt = Date.now();
  return next;
}

function characterProfileLine(character: Archive["characters"][string]) {
  return `${character.id} ${character.identity.name}｜${character.identity.occupation}｜${character.status.current}`;
}
